import { useState, useMemo } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMoneyBillWave,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faHourglassHalf,
  faSearch,
  faFilter,
  faDownload,
  faChevronLeft,
  faChevronRight,
  faStore,
  faEye,
  faTrash,
  faDollarSign,
} from "@fortawesome/free-solid-svg-icons";
import PaymentDetails from "./shop/PaymentDetails";

// ─── Pagination Component ─────────────────────────────────────────────────────

const Pagination = ({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 15, 20, 40, 50, 100, 200, 300],
}) => {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-gray-200">
      <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
        <span>Rows per page:</span>
        <Select
          value={pageSize.toString()}
          onValueChange={(value) => onPageSizeChange(Number(value))}
        >
          <SelectTrigger className="w-16 h-8 border border-gray-300 rounded-lg bg-white text-xs">
            <SelectValue placeholder="10" />
          </SelectTrigger>
          <SelectContent>
            {pageSizeOptions.map((size) => (
              <SelectItem key={size} value={size.toString()}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="ml-2">
          {startItem}-{endItem} of {totalItems}
        </span>
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="h-8 w-8 p-0"
        >
          <span className="text-xs font-bold">⟪</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="h-8 w-8 p-0"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="h-4 w-4" />
        </Button>

        <div className="flex items-center gap-1 px-2">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            return (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? "default" : "ghost"}
                size="sm"
                onClick={() => onPageChange(pageNum)}
                className={`h-8 w-8 p-0 text-xs ${
                  currentPage === pageNum
                    ? "bg-blue-800 text-white hover:bg-blue-900"
                    : "hover:bg-gray-100"
                }`}
              >
                {pageNum}
              </Button>
            );
          })}
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="h-8 w-8 p-0"
        >
          <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="h-8 w-8 p-0"
        >
          <span className="text-xs font-bold">⟫</span>
        </Button>
      </div>
    </div>
  );
};

// ─── Demo Data ────────────────────────────────────────────────────────────────

const subscriptionData = [
  {
    id: 1,
    shopName: "Duka la Hashim - Main Branch",
    shopLocation: "Plot No. 45, Kariakoo Street, Dar es Salaam",
    controllerNumber: "CTRL-001-2024",
    ownerName: "Hashim Bakari Iddi",
    ownerPhone: "+255 712 345 678",
    ownerEmail: "hashim.bakari@example.com",
    subscriptionFee: 10000,
    paymentDate: "2026-01-15",
    dueDate: "2026-02-15",
    status: "Paid",
    paymentMethod: "M-Pesa",
    transactionId: "TXN-2026-001-001",
    paymentType: "Monthly",
    plan: "Premium",
    shopId: 1,
  },
  {
    id: 2,
    shopName: "Duka la Hashim - Branch 2",
    shopLocation: "Plot No. 12, Mwenge Street, Dar es Salaam",
    controllerNumber: "CTRL-002-2024",
    ownerName: "Hashim Bakari Iddi",
    ownerPhone: "+255 712 345 678",
    ownerEmail: "hashim.bakari@example.com",
    subscriptionFee: 10000,
    paymentDate: "2025-12-20",
    dueDate: "2026-01-20",
    status: "Overdue",
    paymentMethod: "Bank Transfer",
    transactionId: "TXN-2025-012-002",
    paymentType: "Monthly",
    plan: "Premium",
    shopId: 2,
  },
  {
    id: 3,
    shopName: "Duka la Hashim - Branch 3",
    shopLocation: "Plot No. 78, Kinondoni Street, Dar es Salaam",
    controllerNumber: "CTRL-003-2024",
    ownerName: "Hashim Bakari Iddi & Partners",
    ownerPhone: "+255 712 345 678",
    ownerEmail: "hashim.partners@example.com",
    subscriptionFee: 10000,
    paymentDate: "2026-01-10",
    dueDate: "2026-02-10",
    status: "Paid",
    paymentMethod: "Tigo Pesa",
    transactionId: "TXN-2026-001-003",
    paymentType: "Monthly",
    plan: "Enterprise",
    shopId: 3,
  },
  {
    id: 4,
    shopName: "Duka la Hashim - Branch 4",
    shopLocation: "Plot No. 5, Mbezi Beach, Dar es Salaam",
    controllerNumber: "CTRL-004-2025",
    ownerName: "Hashim Bakari Iddi",
    ownerPhone: "+255 712 345 678",
    ownerEmail: "hashim.bakari@example.com",
    subscriptionFee: 10000,
    paymentDate: "2025-11-05",
    dueDate: "2025-12-05",
    status: "Finish",
    paymentMethod: "M-Pesa",
    transactionId: "TXN-2025-011-004",
    paymentType: "Monthly",
    plan: "Standard",
    shopId: 4,
  },
  {
    id: 5,
    shopName: "Duka la Hashim - Main Branch",
    shopLocation: "Plot No. 45, Kariakoo Street, Dar es Salaam",
    controllerNumber: "CTRL-001-2024",
    ownerName: "Hashim Bakari Iddi",
    ownerPhone: "+255 712 345 678",
    ownerEmail: "hashim.bakari@example.com",
    subscriptionFee: 10000,
    paymentDate: "2025-12-15",
    dueDate: "2026-01-15",
    status: "Pending",
    paymentMethod: "Bank Transfer",
    transactionId: "TXN-2025-012-005",
    paymentType: "Monthly",
    plan: "Premium",
    shopId: 1,
  },
  {
    id: 6,
    shopName: "Duka la Hashim - Branch 2",
    shopLocation: "Plot No. 12, Mwenge Street, Dar es Salaam",
    controllerNumber: "CTRL-002-2024",
    ownerName: "Hashim Bakari Iddi",
    ownerPhone: "+255 712 345 678",
    ownerEmail: "hashim.bakari@example.com",
    subscriptionFee: 10000,
    paymentDate: "2026-01-20",
    dueDate: "2026-02-20",
    status: "Paid",
    paymentMethod: "Tigo Pesa",
    transactionId: "TXN-2026-001-006",
    paymentType: "Monthly",
    plan: "Premium",
    shopId: 2,
  },
  {
    id: 7,
    shopName: "Duka la Hashim - Branch 3",
    shopLocation: "Plot No. 78, Kinondoni Street, Dar es Salaam",
    controllerNumber: "CTRL-003-2024",
    ownerName: "Hashim Bakari Iddi & Partners",
    ownerPhone: "+255 712 345 678",
    ownerEmail: "hashim.partners@example.com",
    subscriptionFee: 10000,
    paymentDate: "2025-12-10",
    dueDate: "2026-01-10",
    status: "Overdue",
    paymentMethod: "M-Pesa",
    transactionId: "TXN-2025-012-007",
    paymentType: "Monthly",
    plan: "Enterprise",
    shopId: 3,
  },
  {
    id: 8,
    shopName: "Duka la Hashim - Branch 5",
    shopLocation: "Plot No. 3, Tegeta, Dar es Salaam",
    controllerNumber: "CTRL-005-2025",
    ownerName: "Hashim Bakari Iddi",
    ownerPhone: "+255 712 345 678",
    ownerEmail: "hashim.bakari@example.com",
    subscriptionFee: 10000,
    paymentDate: "2025-10-25",
    dueDate: "2025-11-25",
    status: "Finish",
    paymentMethod: "Cash",
    transactionId: "TXN-2025-010-008",
    paymentType: "Monthly",
    plan: "Standard",
    shopId: 5,
  },
  {
    id: 9,
    shopName: "Duka la Hashim - Main Branch",
    shopLocation: "Plot No. 45, Kariakoo Street, Dar es Salaam",
    controllerNumber: "CTRL-001-2024",
    ownerName: "Hashim Bakari Iddi",
    ownerPhone: "+255 712 345 678",
    ownerEmail: "hashim.bakari@example.com",
    subscriptionFee: 10000,
    paymentDate: "2026-02-15",
    dueDate: "2026-03-15",
    status: "Pending",
    paymentMethod: "M-Pesa",
    transactionId: "TXN-2026-002-009",
    paymentType: "Monthly",
    plan: "Premium",
    shopId: 1,
  },
  {
    id: 10,
    shopName: "Duka la Hashim - Branch 6",
    shopLocation: "Plot No. 8, Mbagala, Dar es Salaam",
    controllerNumber: "CTRL-006-2025",
    ownerName: "Hashim Bakari Iddi",
    ownerPhone: "+255 712 345 678",
    ownerEmail: "hashim.bakari@example.com",
    subscriptionFee: 10000,
    paymentDate: "2025-09-15",
    dueDate: "2025-10-15",
    status: "Finish",
    paymentMethod: "Bank Transfer",
    transactionId: "TXN-2025-009-010",
    paymentType: "Monthly",
    plan: "Standard",
    shopId: 6,
  },
];

// ─── Main Component ───────────────────────────────────────────────────────────

const ManagePaymentO = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [shopFilter, setShopFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  // Get unique shops for filter
  const uniqueShops = useMemo(() => {
    const shops = {};
    subscriptionData.forEach((item) => {
      if (!shops[item.shopId]) {
        shops[item.shopId] = item.shopName;
      }
    });
    return shops;
  }, []);

  // Filter data
  const filteredData = useMemo(() => {
    let result = subscriptionData;

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      result = result.filter(
        (item) =>
          item.shopName.toLowerCase().includes(search) ||
          item.controllerNumber.toLowerCase().includes(search) ||
          item.ownerName.toLowerCase().includes(search) ||
          item.ownerPhone.includes(search) ||
          item.ownerEmail.toLowerCase().includes(search) ||
          item.transactionId.toLowerCase().includes(search) ||
          item.paymentMethod.toLowerCase().includes(search),
      );
    }

    if (statusFilter !== "all") {
      result = result.filter((item) => item.status === statusFilter);
    }

    if (shopFilter !== "all") {
      result = result.filter((item) => item.shopId === parseInt(shopFilter));
    }

    return result;
  }, [searchTerm, statusFilter, shopFilter]);

  // Pagination
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const currentData = filteredData.slice(startIndex, startIndex + pageSize);

  // Reset page when filters change
  // useMemo(() => {
  //   setCurrentPage(1);
  // }, [searchTerm, statusFilter, shopFilter, pageSize]);

  // Format currency
   const formatCurrency = (amount) => {
    return new Intl.NumberFormat("sw-TZ", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount) + " TSH";
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      Paid: {
        className: "bg-green-700 text-white border-green-200",
        icon: faCheckCircle,
        iconColor: "text-green-500",
        dotColor: "bg-green-500",
        label: "Paid",
      },
      Pending: {
        className: "bg-orange-600 text-white border-yellow-200",
        icon: faHourglassHalf,
        iconColor: "text-yellow-500",
        dotColor: "bg-yellow-500",
        label: "Pending",
      },
      Overdue: {
        className: "bg-red-600 text-white border-red-200",
        icon: faExclamationTriangle,
        iconColor: "text-red-500",
        dotColor: "bg-red-500",
        label: "Overdue",
      },
      Finish: {
        className: "bg-gray-600 text-white border-gray-200",
        icon: faTimesCircle,
        iconColor: "text-gray-500",
        dotColor: "bg-gray-500",
        label: "Finish",
      },
    };

    return statusConfig[status] || statusConfig.Pending;
  };

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const totalPaid = subscriptionData.filter(
      (item) => item.status === "Paid",
    ).length;
    const totalPending = subscriptionData.filter(
      (item) => item.status === "Pending",
    ).length;
    const totalOverdue = subscriptionData.filter(
      (item) => item.status === "Overdue",
    ).length;
    const totalFinish = subscriptionData.filter(
      (item) => item.status === "Finish",
    ).length;
    const totalRevenue = subscriptionData.reduce(
      (sum, item) => sum + item.subscriptionFee,
      0,
    );

    return {
      totalPaid,
      totalPending,
      totalOverdue,
      totalFinish,
      totalRevenue,
      totalSubscriptions: subscriptionData.length,
    };
  }, []);

  // Status tabs for filtering
  const statusTabs = [
    { id: "all", label: "All", count: subscriptionData.length },
    { id: "Paid", label: "Paid", count: summaryStats.totalPaid },
    { id: "Pending", label: "Pending", count: summaryStats.totalPending },
    { id: "Overdue", label: "Overdue", count: summaryStats.totalOverdue },
    { id: "Finish", label: "Finish", count: summaryStats.totalFinish },
  ];

  return (
    <div className="flex flex-col gap-4 sm:gap-6 max-w-8xl mx-auto px-3 sm:px-4 lg:px-0">
      {/* Header */}
      <Card className="bg-white border border-gray-200 px-3 sm:px-4 lg:px-6 py-3 sm:py-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
              <FontAwesomeIcon
                icon={faMoneyBillWave}
                className="text-blue-800 h-4 w-4 sm:h-5 sm:w-5"
              />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900">
                Manage payments
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                Track and manage subscription payments
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className="bg-green-50 text-green-700 border-green-200 text-[10px] sm:text-xs"
            >
              <FontAwesomeIcon icon={faDollarSign} className="mr-1 h-3 w-3" />
              Revenue: {formatCurrency(summaryStats.totalRevenue)}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              className="gap-1 border-gray-300 text-xs sm:text-sm"
            >
              <FontAwesomeIcon icon={faDownload} />
              <span className="hidden sm:inline">Export</span>
            </Button>
          </div>
        </div>
      </Card>

      {/* Summary Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3 lg:gap-4">
        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center">
          <p className="text-sm text-gray-600 font-medium">Total</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
            {summaryStats.totalSubscriptions}
          </p>
          <Badge
            variant="outline"
            className="mt-0.5 sm:mt-1 text-[8px] sm:text-[10px] bg-blue-800 text-white border-blue-200"
          >
            Subscriptions
          </Badge>
        </Card>

        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center">
          <p className="text-sm text-gray-600 font-medium">Paid</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-600">
            {summaryStats.totalPaid}
          </p>
          <Badge
            variant="outline"
            className="mt-0.5 sm:mt-1 text-[8px] sm:text-[10px] bg-green-600 text-white border-green-200"
          >
            <FontAwesomeIcon icon={faCheckCircle} className="mr-0.5 h-2 w-2" />
            Completed
          </Badge>
        </Card>

        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center">
          <p className="text-sm text-gray-600 font-medium">Pending</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-yellow-600">
            {summaryStats.totalPending}
          </p>
          <Badge
            variant="outline"
            className="mt-0.5 sm:mt-1 text-[8px] sm:text-[10px] bg-orange-600 text-white border-yellow-200"
          >
            <FontAwesomeIcon
              icon={faHourglassHalf}
              className="mr-0.5 h-2 w-2"
            />
            Waiting
          </Badge>
        </Card>

        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center">
          <p className="text-sm text-gray-600 font-medium">Overdue</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-red-600">
            {summaryStats.totalOverdue}
          </p>
          <Badge
            variant="outline"
            className="mt-0.5 sm:mt-1 text-[8px] sm:text-[10px] bg-red-600 text-white border-red-200"
          >
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="mr-0.5 h-2 w-2"
            />
            Overdue
          </Badge>
        </Card>

        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center col-span-2 sm:col-span-4 lg:col-span-1">
          <p className="text-sm text-gray-700 font-medium">Finish</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-600">
            {summaryStats.totalFinish}
          </p>
          <Badge
            variant="outline"
            className="mt-0.5 sm:mt-1 text-[8px] sm:text-[10px] bg-purple-600 text-white border-gray-200"
          >
            <FontAwesomeIcon icon={faTimesCircle} className="mr-0.5 h-2 w-2" />
            Ended
          </Badge>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-gray-200 p-3 sm:p-4 lg:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-3.5 w-3.5 sm:h-4 sm:w-4"
            />
            <Input
              type="text"
              placeholder="Search by shop, controller, owner, phone, email, transaction..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-3 py-1.5 h-9 sm:h-10 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-800/30 w-full"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Select value={shopFilter} onValueChange={setShopFilter}>
              <SelectTrigger className="w-full sm:w-48 h-9 sm:h-10 border border-gray-300 rounded-lg bg-white text-xs sm:text-sm">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faStore}
                    className="text-gray-400 h-3 w-3"
                  />
                  <SelectValue placeholder="All Shops" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Shops</SelectItem>
                {Object.entries(uniqueShops).map(([id, name]) => (
                  <SelectItem key={id} value={id}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-36 h-9 sm:h-10 border border-gray-300 rounded-lg bg-white text-xs sm:text-sm">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faFilter}
                    className="text-gray-400 h-3 w-3"
                  />
                  <SelectValue placeholder="Status" />
                </div>
              </SelectTrigger>
              <SelectContent>
                {statusTabs.map((tab) => (
                  <SelectItem key={tab.id} value={tab.id}>
                    {tab.label} ({tab.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Status Quick Filters */}
        <div className="flex flex-wrap items-center gap-1 mt-3 pt-3 border-t border-gray-100">
          {statusTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setStatusFilter(tab.id)}
              className={`px-2 sm:px-3 py-1 rounded-md text-[10px] sm:text-xs font-medium transition-all duration-200 ${
                statusFilter === tab.id
                  ? "bg-blue-800 text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      </Card>

      {/* Table */}
      <Card className="bg-white border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-3 sm:px-4 lg:px-6 pt-3 sm:pt-4 pb-2 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="text-xs sm:text-sm text-gray-500">
              Showing{" "}
              <span className="font-semibold text-gray-700">{totalItems}</span>{" "}
              items
            </div>
            <div className="text-xs sm:text-sm text-gray-400">
              {currentData.length > 0 && `Page ${currentPage} of ${totalPages}`}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead>
              <tr className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
                <th className="text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                  #
                </th>
                <th className="text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                  Control number
                </th>
                <th className="text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden md:table-cell">
                  Shop
                </th>
                <th className="text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden lg:table-cell">
                  Owner
                </th>
                <th className="text-center text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                  Amount
                </th>
                <th className="text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden sm:table-cell">
                  Due Date
                </th>
                <th className="text-center text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                  Status
                </th>
                <th className="text-center text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {currentData.length > 0 ? (
                currentData.map((item, i) => {
                  const status = getStatusBadge(item.status);
                  const globalIndex = startIndex + i + 1;
                  return (
                    <tr
                      key={item.id}
                      className="hover:bg-blue-50/50 transition-colors cursor-pointer"
                      onClick={() => {
                        setSelectedPayment(item);
                        setViewModalOpen(true);
                      }}
                    >
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-xs text-gray-700 font-medium">
                        {globalIndex}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-800 text-xs sm:text-sm">
                        {item.controllerNumber}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm text-gray-700 hidden md:table-cell truncate max-w-[120px]">
                        {item.shopName}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm text-gray-700 hidden lg:table-cell">
                        {item.ownerName}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-center font-semibold text-gray-900 text-xs sm:text-sm">
                        {formatCurrency(item.subscriptionFee)}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm text-gray-700 hidden sm:table-cell">
                        {new Date(item.dueDate).toLocaleDateString()}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-center">
                        <Badge
                          variant="outline"
                          className={`${status.className} border font-normal px-1.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-[8px] sm:text-[11px] flex items-center gap-0.5 sm:gap-1.5 justify-center`}
                        >
                          <span
                            className={`inline-block w-1.5 h-1.5 rounded-full ${status.dotColor}`}
                          ></span>
                          {item.status}
                        </Badge>
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4">
                        <div className="flex gap-1 sm:gap-2 justify-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-blue-600 hover:bg-blue-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPayment(item);
                              setViewModalOpen(true);
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faEye}
                              className="h-3 w-3 sm:h-4 sm:w-4"
                            />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-red-600 hover:bg-red-50"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <FontAwesomeIcon
                              icon={faTrash}
                              className="h-3 w-3 sm:h-4 sm:w-4"
                            />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className="text-center py-6 sm:py-8">
                    <div className="flex flex-col items-center gap-2">
                      <div className="text-gray-400 text-xs sm:text-sm">
                        No payments found
                      </div>
                      <p className="text-gray-400 text-[10px] sm:text-xs">
                        {searchTerm ||
                        statusFilter !== "all" ||
                        shopFilter !== "all"
                          ? "Try adjusting your search or filters"
                          : "No payment records available"}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalItems > 0 && (
          <div className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        )}
      </Card>

      {/* Payment Details Modal */}
      {viewModalOpen && selectedPayment && (
        <PaymentDetails
          payment={selectedPayment}
          onClose={() => {
            setViewModalOpen(false);
            setSelectedPayment(null);
          }}
        />
      )}
    </div>
  );
};

export default ManagePaymentO;
